"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useHeader } from "@/context/HeaderContext";
import type { HeaderProps } from "./Header.props";
import { HeaderLogo } from "./HeaderLogo";
import { DesktopNav } from "./DesktopNav";
import { HamburgerButton } from "./HamburgerButton";
import { MobileNav } from "./MobileNav";
import { Box } from "@/components/atoms/Box";

/**
 * Header component with Transparent and White modes.
 *
 * Layout Shift Prevention:
 * - All size-related properties (height, padding, logo size, nav typography)
 *   remain constant across both modes to prevent layout shifts.
 * - Only color-related properties transition between modes.
 * - Border and shadow are always rendered with consistent dimensions;
 *   only their colors change between modes.
 */
export const HeaderDefault = t0 => {
  const $ = _c(36);
  const {
    className,
    logo,
    links: t1,
    contactLink
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const links = t2;
  const {
    isTransparentEnabled
  } = useHeader();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isActuallyTransparent = isTransparentEnabled && !isScrolled && !isMenuOpen;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 20);
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== className || $[5] !== isActuallyTransparent) {
    t5 = cn("fixed top-0 left-0 z-100 w-full", "py-2", "border-b", "transition-[background-color,border-color,box-shadow,backdrop-filter] duration-200 ease-in-out", isActuallyTransparent ? ["bg-transparent", "border-transparent", "shadow-[var(--aq-shadow-transparent)]"] : ["bg-background/90 backdrop-blur-lg", "border-border", "shadow-sm"], className);
    $[4] = className;
    $[5] = isActuallyTransparent;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isActuallyTransparent) {
    t6 = isActuallyTransparent && <Box className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/50 to-transparent" aria-hidden="true" />;
    $[7] = isActuallyTransparent;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isActuallyTransparent || $[10] !== logo) {
    t7 = <HeaderLogo logo={logo} isTransparent={isActuallyTransparent} />;
    $[9] = isActuallyTransparent;
    $[10] = logo;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== contactLink || $[13] !== isActuallyTransparent || $[14] !== links) {
    t8 = <DesktopNav links={links} contactLink={contactLink} isTransparent={isActuallyTransparent} />;
    $[12] = contactLink;
    $[13] = isActuallyTransparent;
    $[14] = links;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <Box className="container relative mx-auto flex items-center justify-between px-4 lg:px-6">{t7}{t8}</Box>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isMenuOpen) {
    t10 = () => setIsMenuOpen(!isMenuOpen);
    $[19] = isMenuOpen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isActuallyTransparent || $[22] !== isMenuOpen || $[23] !== t10) {
    t11 = <Box className="absolute inset-y-0 right-4 flex items-center lg:hidden z-101"><HamburgerButton isOpen={isMenuOpen} onToggle={t10} isTransparent={isActuallyTransparent} /></Box>;
    $[21] = isActuallyTransparent;
    $[22] = isMenuOpen;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsMenuOpen(false);
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== contactLink || $[27] !== isMenuOpen || $[28] !== links) {
    t13 = <MobileNav isOpen={isMenuOpen} links={links} contactLink={contactLink} onClose={t12} />;
    $[26] = contactLink;
    $[27] = isMenuOpen;
    $[28] = links;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t11 || $[31] !== t13 || $[32] !== t5 || $[33] !== t6 || $[34] !== t9) {
    t14 = <header role="banner" aria-label="Site header" className={t5}>{t6}{t9}{t11}{t13}</header>;
    $[30] = t11;
    $[31] = t13;
    $[32] = t5;
    $[33] = t6;
    $[34] = t9;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};